import { RouteBasePath } from 'components/mainLayout/routeBasePath';
import DashboardPage from 'pages/admin/dashboardPage';
import AddNewNewsPage from 'pages/admin/newsManagePage/addNewNewsPage';
import AboutUsPage from 'pages/client/aboutUs';
import { lazy } from 'react';

import { ADMIN_BASE_PATH } from '@app/common/helpers';

const PageNotFound = lazy(() => import('pages/pageNotFound'));
const ClientRoot = lazy(() => import('pages/client'));
const ComingSoon = lazy(() => import('pages/client/comingSoon'));
const ProductsPageRoot = lazy(() => import('pages/client/products'));
const AllProducts = lazy(() => import('pages/client/products/allProducts'));
const ProductDetail = lazy(() => import('pages/client/products/productDetail'));
const AuthenticationPageRoot = lazy(() => import('pages/authentication'));
const Login = lazy(() => import('pages/authentication/login'));
const AdminRoot = lazy(() => import('pages/admin'));
const Translation = lazy(() => import('pages/admin/translationManage'));
const ManageAccount = lazy(() => import('pages/admin/manageAccount'));
const UserAccountDetail = lazy(() => import('pages/admin/userAccountDetail'));
const ManageCategory = lazy(() => import('pages/admin/categoryManage'));
const ManageProduct = lazy(() => import('pages/admin/productManage'));
const AddNewProductPage = lazy(() => import('pages/admin/productManage/addNewProductPage'));
const ProductDetailPage = lazy(() => import('pages/admin/productManage/viewProductDetailPage'));
const EditProductPage = lazy(() => import('pages/admin/productManage/editProductPage'));
const SimilarProductsPage = lazy(() => import('pages/client/products/similarProducts'));
const HomepageContentManage = lazy(() => import('pages/admin/manageContent/homepageContent'));
const BestSellerProductManage = lazy(() => import('pages/admin/manageContent/bestSellerProductManage'));
const FeaturedProductManage = lazy(() => import('pages/admin/manageContent/featuredProductManage'));
const NewsManagePage = lazy(() => import('pages/admin/newsManagePage'));
const NewsDetailAdminPage = lazy(() => import('pages/admin/newsManagePage/newsDetailPage'));
const UpdateNewsPage = lazy(() => import('pages/admin/newsManagePage/updateNewsPage'));
const NewsClientRootPage = lazy(() => import('pages/client/news'));
const NewsDetailClientPage = lazy(() => import('pages/client/news/newsDetail'));

export interface IRoute {
  label: string;
  path: string;
  isPrivate: boolean;
  element: any;
  children: IChildRoute[];
}

export interface IChildRoute extends Omit<IRoute, 'isPrivate' | 'children'> {
  isIndex: boolean;
}

export const routes: IRoute[] = [
  {
    label: 'header.home',
    path: RouteBasePath.CLIENT_BASE_URL,
    isPrivate: false,
    element: <ClientRoot />,
    children: []
  },
  {
    label: 'header.product', // using i18n key for label
    path: RouteBasePath.PRODUCT_PAGE_PATH,
    isPrivate: false,
    element: <ProductsPageRoot />,
    children: [
      {
        label: '',
        path: '',
        element: <AllProducts />,
        isIndex: true
      },
      {
        label: 'labels.product_similar',
        path: RouteBasePath.SIMILAR_PRODUCT_PAGE_PATH,
        element: <SimilarProductsPage />,
        isIndex: false
      }
    ]
  },
  {
    label: 'labels.product_detail',
    path: RouteBasePath.PRODUCT_DETAIL_PAGE_PATH,
    element: <ProductDetail />,
    isPrivate: false,
    children: []
  },
  {
    label: 'header.about',
    path: RouteBasePath.ABOUT_US_PAGE_PATH,
    isPrivate: false,
    element: <AboutUsPage />,
    children: []
  },
  {
    label: 'labels.authentication',
    path: RouteBasePath.AUTH_PAGE_PATH,
    element: <AuthenticationPageRoot />,
    isPrivate: false,
    children: [
      {
        label: 'labels.login',
        path: RouteBasePath.LOGIN_PAGE_PATH,
        element: <Login />,
        isIndex: true
      }
    ]
  },
  {
    label: 'admin.label.admin',
    path: ADMIN_BASE_PATH,
    isPrivate: true,
    element: <AdminRoot />,
    children: [
      {
        label: '',
        path: '',
        element: <DashboardPage />,
        isIndex: true
      },
      {
        label: 'admin.label.translation',
        path: RouteBasePath.TRANSLATION_PAGE_PATH,
        element: <Translation />,
        isIndex: false
      },
      {
        label: 'admin.label.account_manage',
        path: RouteBasePath.MANAGE_ACCOUNT_PAGE_PATH,
        element: <ManageAccount />,
        isIndex: false
      },
      {
        label: 'admin.label.account_detail',
        path: `${RouteBasePath.MANAGE_ACCOUNT_PAGE_PATH}/:accountId`,
        element: <UserAccountDetail />,
        isIndex: false
      },
      {
        label: 'admin.label.category_manage',
        path: RouteBasePath.CATEGORY_MANAGE_PAGE_PATH,
        element: <ManageCategory />,
        isIndex: false
      },
      {
        label: 'admin.label.product_manage',
        path: RouteBasePath.PRODUCT_MANAGE_PAGE_PATH,
        element: <ManageProduct />,
        isIndex: false
      },
      {
        label: 'admin.label.add_new_product',
        path: `${RouteBasePath.PRODUCT_MANAGE_PAGE_PATH}/${RouteBasePath.ADD_PRODUCT_PAGE_PATH}`,
        element: <AddNewProductPage />,
        isIndex: false
      },
      {
        label: 'admin.label.product_detail',
        path: `${RouteBasePath.PRODUCT_MANAGE_PAGE_PATH}/:productId`,
        element: <ProductDetailPage />,
        isIndex: false
      },
      {
        label: 'admin.label.edit_product',
        path: `${RouteBasePath.PRODUCT_MANAGE_PAGE_PATH}/${RouteBasePath.EDIT_PRODUCT_PAGE_PATH}/:productId`,
        element: <EditProductPage />,
        isIndex: false
      },
      {
        label: 'admin.label.content_manage_homepage',
        path: RouteBasePath.HOMEPAGE_CONTENT_MANAGE_PATH,
        element: <HomepageContentManage />,
        isIndex: false
      },
      {
        label: 'admin.label.content_manage_best_seller_products',
        path: RouteBasePath.BEST_SELLER_PRODUCT_MANAGE_PATH,
        element: <BestSellerProductManage />,
        isIndex: false
      },
      {
        label: 'admin.label.content_manage_featured_products',
        path: RouteBasePath.FEATURED_PRODUCT_MANAGE_PATH,
        element: <FeaturedProductManage />,
        isIndex: false
      },
      {
        label: 'admin.label.news_manage',
        path: RouteBasePath.NEWS_MANAGE_PAGE_PATH,
        element: <NewsManagePage />,
        isIndex: false
      },
      {
        label: 'admin.label.add_new_news',
        path: `${RouteBasePath.NEWS_MANAGE_PAGE_PATH}/${RouteBasePath.ADD_NEW_NEWS_PAGE_PATH}`,
        element: <AddNewNewsPage />,
        isIndex: false
      },
      {
        label: 'admin.label.news_detail',
        path: `${RouteBasePath.NEWS_MANAGE_PAGE_PATH}/${RouteBasePath.NEWS_DETAIL_PAGE_PATH}/:newsId`,
        element: <NewsDetailAdminPage />,
        isIndex: false
      },
      {
        label: 'admin.label.update_news',
        path: `${RouteBasePath.NEWS_MANAGE_PAGE_PATH}/${RouteBasePath.EDIT_NEWS_PAGE_PATH}/:newsId`,
        element: <UpdateNewsPage />,
        isIndex: false
      }
    ]
  },
  {
    label: 'header.news',
    path: RouteBasePath.NEWS_LISTING_PAGE_PATH,
    isPrivate: false,
    element: <NewsClientRootPage />,
    children: []
  },
  {
    label: '',
    path: `${RouteBasePath.NEWS_LISTING_PAGE_PATH}/${RouteBasePath.NEWS_DETAIL_PAGE_PATH}/:newsId`,
    isPrivate: false,
    element: <NewsDetailClientPage />,
    children: []
  },
  {
    label: '',
    path: RouteBasePath.COMING_SOON_PAGE_PATH,
    isPrivate: false,
    element: <ComingSoon />,
    children: []
  },
  {
    label: '',
    path: '*',
    isPrivate: false,
    element: <PageNotFound />,
    children: []
  }
];
